import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Logo } from "@/components/Logo";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X, Save, Upload, BookOpen, ExternalLink, Calendar, Tag, Database } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { adminUpload, getAdminToken } from "@/lib/adminAuth";
import { AdminLoginGate } from "@/components/AdminLoginGate";
import type { BlogPost } from "@shared/schema";
import {
  getBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  importBlogPost,
  importDocumentation,
} from "@/lib/blogFirestore";

const CATEGORIES = ["General", "App Updates", "News & Events", "Business Tips"];

const CATEGORY_COLORS: Record<string, string> = {
  "App Updates": "bg-blue-50 text-blue-700",
  "News & Events": "bg-green-50 text-green-700",
  "Business Tips": "bg-amber-50 text-amber-700",
  "General": "bg-slate-100 text-slate-600",
};

const CATEGORY_GRADIENTS: Record<string, string> = {
  "App Updates": "from-blue-500 to-blue-700",
  "News & Events": "from-green-500 to-emerald-700",
  "Business Tips": "from-amber-500 to-orange-600",
  "General": "from-slate-400 to-slate-600",
};

function slugify(text: string) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "General",
  coverImageUrl: "",
  authorName: "DailyHisaab Team",
  published: false,
};

function BlogAdminContent() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [contentTab, setContentTab] = useState<"write" | "preview">("write");
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null);
  const [migrating, setMigrating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const QUERY_KEY = ["firestore/blog_posts/all"];

  const { data: posts = [], isLoading } = useQuery<BlogPost[]>({
    queryKey: QUERY_KEY,
    queryFn: () => getBlogPosts(false),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof emptyForm) =>
      createBlogPost({
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt || null,
        content: data.content,
        category: data.category,
        coverImageUrl: data.coverImageUrl || null,
        authorName: data.authorName,
        published: data.published,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["firestore/blog_posts/published"] });
      setShowForm(false);
      setForm(emptyForm);
      toast({ title: "Post created!" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof emptyForm }) =>
      updateBlogPost(id, {
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt || null,
        content: data.content,
        category: data.category,
        coverImageUrl: data.coverImageUrl || null,
        authorName: data.authorName,
        published: data.published,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["firestore/blog_posts/published"] });
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      toast({ title: "Post updated!" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBlogPost(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["firestore/blog_posts/published"] });
      toast({ title: "Post deleted" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminUpload(formData);
      if (!res.ok) throw new Error((await res.json()).message);
      const { url } = await res.json();
      setForm((f) => ({ ...f, coverImageUrl: url }));
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleMigrateFromPg = async () => {
    if (!confirm("This will import blog posts and documentation from the old database into Firestore. Any existing Firestore data will NOT be overwritten. Continue?")) return;
    setMigrating(true);
    try {
      const token = await getAdminToken();
      if (!token) throw new Error("Not authenticated");
      const res = await fetch("/api/admin/pg-data", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch legacy data");
      const { blogPosts, documentation } = await res.json();

      let imported = 0;
      for (const post of blogPosts) {
        await importBlogPost(post);
        imported++;
      }
      for (const doc of documentation) {
        await importDocumentation(doc.type, doc);
        imported++;
      }

      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["firestore/blog_posts/published"] });
      toast({ title: `Migration complete`, description: `${imported} record(s) imported from legacy database.` });
    } catch (e: any) {
      toast({ title: "Migration failed", description: e.message, variant: "destructive" });
    } finally {
      setMigrating(false);
    }
  };

  const handleNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setContentTab("write");
    setShowUrlInput(false);
    setShowForm(true);
  };

  const handleEdit = (post: BlogPost) => {
    setEditing(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || "",
      content: post.content,
      category: post.category || "General",
      coverImageUrl: post.coverImageUrl || "",
      authorName: post.authorName || "DailyHisaab Team",
      published: post.published || false,
    });
    setContentTab("write");
    setShowUrlInput(false);
    setShowForm(true);
  };

  const handleTitleChange = (val: string) => {
    setForm((f) => ({ ...f, title: val, slug: editing ? f.slug : slugify(val) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const handlePreview = (post: BlogPost) => {
    if (post.published) {
      window.open(`/blogs/${post.slug}`, "_blank");
    } else {
      setPreviewPost(post);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-[#8B2121]/10 dark:border-white/10 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Logo size="md" />
          <span className="text-xs font-bold text-[#8B2121] bg-[#8B2121]/10 px-2.5 py-1 rounded-full">Blog Admin</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 pb-16">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Posts</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleMigrateFromPg}
              disabled={migrating}
              data-testid="button-migrate-pg"
              title="Import from legacy database (run once)"
              className="flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-bold border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-[#8B2121]/40 hover:text-[#8B2121] transition-colors disabled:opacity-60"
            >
              {migrating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
              {migrating ? "Migrating..." : "Import Legacy"}
            </button>
            <button
              onClick={handleNew}
              data-testid="button-new-post"
              className="flex items-center gap-1.5 px-4 py-2 rounded-2xl text-white text-sm font-bold active:scale-[0.98] transition-transform shadow-sm"
              style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
            >
              <Plus className="h-4 w-4" />
              New Post
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#8B2121]" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <p className="font-medium">No posts yet. Create your first post!</p>
          </div>
        ) : (
          <div className="space-y-3 mb-8">
            {posts.map((post) => (
              <div
                key={post.id}
                data-testid={`row-post-${post.id}`}
                className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{post.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{post.category}</span>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                      post.published
                        ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"
                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                    }`}>
                      {post.published ? "Published" : "Draft"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handlePreview(post)}
                    data-testid={`button-preview-${post.id}`}
                    title={post.published ? "Open post" : "Preview draft"}
                    className="p-2 rounded-xl text-slate-400 hover:text-blue-500 transition-colors"
                  >
                    {post.published ? <ExternalLink className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleEdit(post)}
                    data-testid={`button-edit-${post.id}`}
                    className="p-2 rounded-xl text-slate-400 hover:text-[#8B2121] transition-colors"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { if (confirm("Delete this post?")) deleteMutation.mutate(post.id); }}
                    data-testid={`button-delete-${post.id}`}
                    className="p-2 rounded-xl text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-slate-900 dark:text-white">{editing ? "Edit Post" : "New Post"}</h2>
              <button
                onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Title</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  data-testid="input-post-title"
                  placeholder="Post title"
                  className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Slug (URL)</label>
                <input
                  type="text"
                  required
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  data-testid="input-post-slug"
                  placeholder="post-url-slug"
                  className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    data-testid="select-post-category"
                    className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Author</label>
                  <input
                    type="text"
                    value={form.authorName}
                    onChange={(e) => setForm((f) => ({ ...f, authorName: e.target.value }))}
                    data-testid="input-post-author"
                    className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Cover Image</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
                />
                {form.coverImageUrl ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                    <img src={form.coverImageUrl} alt="Cover" className="w-full h-36 object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, coverImageUrl: "" }))}
                      className="absolute top-2 right-2 p-1 bg-black/60 text-white rounded-lg hover:bg-black/80 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-2 right-2 px-2.5 py-1 bg-black/60 text-white text-xs font-bold rounded-lg hover:bg-black/80 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="button-upload-cover"
                      className="w-full h-24 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:border-[#8B2121]/40 hover:text-[#8B2121] transition-colors disabled:opacity-60"
                    >
                      {uploading ? (
                        <><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">Uploading...</span></>
                      ) : (
                        <><Upload className="h-5 w-5" /><span className="text-xs font-medium">Click to upload image</span><span className="text-xs opacity-60">PNG, JPG, WebP up to 5MB</span></>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUrlInput(!showUrlInput)}
                      className="text-xs text-slate-400 hover:text-[#8B2121] transition-colors"
                    >
                      {showUrlInput ? "Hide URL input" : "Or paste image URL instead"}
                    </button>
                    {showUrlInput && (
                      <input
                        type="url"
                        value={form.coverImageUrl}
                        onChange={(e) => setForm((f) => ({ ...f, coverImageUrl: e.target.value }))}
                        data-testid="input-post-cover-url"
                        placeholder="https://..."
                        className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20"
                      />
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Excerpt (1-2 sentences)</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  data-testid="input-post-excerpt"
                  rows={2}
                  placeholder="Brief summary shown in the blog list..."
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 resize-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Content (HTML)</label>
                  <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
                    <button
                      type="button"
                      onClick={() => setContentTab("write")}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                        contentTab === "write"
                          ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      Write
                    </button>
                    <button
                      type="button"
                      onClick={() => setContentTab("preview")}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                        contentTab === "preview"
                          ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      Preview
                    </button>
                  </div>
                </div>

                {contentTab === "write" ? (
                  <textarea
                    required
                    value={form.content}
                    onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                    data-testid="input-post-content"
                    rows={12}
                    placeholder="<h2>Introduction</h2><p>Write your post in HTML format...</p>"
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-mono text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 resize-y"
                  />
                ) : (
                  <div className="w-full min-h-[200px] px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    {form.content ? (
                      <div
                        className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-[#8B2121] prose-img:rounded-2xl text-sm"
                        dangerouslySetInnerHTML={{ __html: form.content }}
                      />
                    ) : (
                      <p className="text-slate-400 text-sm italic">Nothing to preview yet — write some HTML in the Write tab.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, published: !f.published }))}
                  data-testid="toggle-published"
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-bold border transition-colors ${
                    form.published
                      ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900"
                      : "bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  {form.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  {form.published ? "Published" : "Draft"}
                </button>

                <button
                  type="submit"
                  disabled={isSaving}
                  data-testid="button-save-post"
                  className="flex items-center gap-1.5 px-5 py-2 rounded-2xl text-white text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60 ml-auto"
                  style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
                >
                  {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Save className="h-4 w-4" /> Save Post</>}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {previewPost && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col">
          <div className="bg-[#FFFBF2] dark:bg-[#1A1614] px-4 py-3 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-800">
                Draft Preview
              </span>
              <span className="text-sm text-slate-500 truncate max-w-48">{previewPost.title}</span>
            </div>
            <button
              onClick={() => setPreviewPost(null)}
              className="p-2 rounded-xl text-slate-500 hover:text-slate-800 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bg-[#FFFBF2] dark:bg-[#1A1614]">
            <div className="max-w-2xl mx-auto px-4 py-6 pb-16">
              {previewPost.coverImageUrl ? (
                <img src={previewPost.coverImageUrl} alt={previewPost.title} className="w-full h-56 object-cover rounded-3xl mb-6" />
              ) : (
                <div className={`h-44 rounded-3xl bg-gradient-to-br ${CATEGORY_GRADIENTS[previewPost.category || "General"] || CATEGORY_GRADIENTS["General"]} flex items-center justify-center mb-6`}>
                  <BookOpen className="h-12 w-12 text-white/60" />
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  <Tag className="h-3 w-3 inline mr-1" />
                  {previewPost.category || "General"}
                </span>
                {previewPost.createdAt && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(previewPost.createdAt)}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{previewPost.title}</h1>
              {previewPost.authorName && (
                <p className="text-sm text-slate-400 mb-6">By {previewPost.authorName}</p>
              )}
              <div
                className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-[#8B2121] prose-img:rounded-2xl text-slate-700 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: previewPost.content }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BlogAdmin() {
  return (
    <AdminLoginGate label="Blog Admin">
      <BlogAdminContent />
    </AdminLoginGate>
  );
}
